import { get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { type QueryClient, useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { z } from "zod";

const workerProfilePictureResponseSchema = z.object({
  distribution_url: z.string().optional(),
});

export type WorkerProfilePictureResponse = z.infer<typeof workerProfilePictureResponseSchema>;

export function getAgentProfilePictureUrl(workerId: string): string {
  return `${environmentConfig.REACT_APP_BASE_API_URL}/agentProfile/${workerId}/profile-image-distribution-url`;
}

export function resetAgentProfilePictureQuery(
  queryClient: QueryClient,
  {
    workerId,
  }: {
    workerId: string;
  }
) {
  void queryClient.resetQueries({ queryKey: getQueryKey(workerId) });
}

function getQueryKey(workerId: string) {
  return [getAgentProfilePictureUrl(workerId)];
}

export function useWorkerProfilePicture(
  workerId: string,
  options: UseQueryOptions<WorkerProfilePictureResponse> = {}
) {
  return useQuery<WorkerProfilePictureResponse>({
    queryKey: getQueryKey(workerId),
    queryFn: async () => {
      try {
        const response = await get({
          url: getAgentProfilePictureUrl(workerId),
          responseSchema: workerProfilePictureResponseSchema,
        });

        return response.data;
      } catch (error) {
        if (error instanceof AxiosError && error.response?.status === 404) {
          // Return undefined distribution_url instead of failing if the user doesn't have a profile picture
          return { distribution_url: undefined };
        }

        throw error;
      }
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_AGENT_PROFILE_PICTURE_FAILURE,
    },
    ...options,
  });
}
